import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import {
  buildBreadcrumbJsonLd,
  buildCanonicalUrl,
  buildWebPageJsonLd,
  organizationJsonLd,
  websiteJsonLd,
} from "@/lib/seo";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Terms of Service | Travel Wings Global"
        description="Review the terms of service governing Travel Wings Global forex and travel offerings."
        path="/terms-of-service"
        structuredData={[
          organizationJsonLd,
          websiteJsonLd,
          buildWebPageJsonLd({
            name: "Terms of Service | Travel Wings Global",
            description:
              "Terms governing Travel Wings Global forex and travel services.",
            url: buildCanonicalUrl("/terms-of-service"),
            type: "TermsOfService",
          }),
          buildBreadcrumbJsonLd([
            { name: "Home", url: buildCanonicalUrl("/") },
            { name: "Terms of Service", url: buildCanonicalUrl("/terms-of-service") },
          ]),
        ]}
      />
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-sm text-gray-500 mb-8">Last updated: January 1, 2025</p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                By accessing and using the services provided by Travel Wings Forex Private Limited (formerly known as 
                Make My Travel (India) Pvt. Ltd.), you accept and agree to be bound by the terms and provision of this agreement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Company Information</h2>
              <p className="mb-4">
                Travel Wings Forex Pvt Ltd was established in 2010 and is a leading corporate travel company. We are 
                recognized by IATA, Ministry of Tourism Govt. of India, IATO, TAAI, and TAAFI. We hold an RBI-approved 
                AD II Category License authorizing us to deal with foreign currency.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Services Provided</h2>
              <p className="mb-4">We provide the following services:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>International and Domestic Air Ticketing</li>
                <li>Hotel Reservation services</li>
                <li>Holiday Travel Packages</li>
                <li>Car Rental services</li>
                <li>Foreign Exchange services</li>
                <li>Money Transfer & Remittance</li>
                <li>Visa Assistance services</li>
                <li>Travel Insurance</li>
                <li>24x7 Emergency Services</li>
                <li>Cruise Booking</li>
                <li>Group Travel arrangements</li>
                <li>Corporate Travel Management</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Booking and Payment Terms</h2>
              <h3 className="text-xl font-medium text-gray-800 mb-3">4.1 Booking Confirmation</h3>
              <p className="mb-4">
                All bookings are subject to availability and confirmation. We reserve the right to cancel or modify 
                bookings in case of unforeseen circumstances.
              </p>
              
              <h3 className="text-xl font-medium text-gray-800 mb-3">4.2 Payment</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Full payment is required at the time of booking unless otherwise specified</li>
                <li>All prices are subject to change without prior notice</li>
                <li>Additional charges may apply for changes or cancellations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Cancellation and Refund Policy</h2>
              <ul className="list-disc pl-6 mb-4">
                <li>Cancellation charges apply as per the terms of individual service providers</li>
                <li>Refunds are processed according to the cancellation policy of airlines, hotels, and other suppliers</li>
                <li>Processing fees are non-refundable</li>
                <li>Refunds may take 7-21 working days to process</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Travel Documentation</h2>
              <p className="mb-4">
                Customers are responsible for ensuring they have valid travel documents including:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Valid passport with required validity</li>
                <li>Appropriate visas for destination countries</li>
                <li>Health certificates and vaccinations as required</li>
                <li>Travel insurance coverage</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="mb-4">
                Travel Wings Forex Private Limited acts as an agent for various service providers. Our liability is 
                limited to the amount paid for our services. We are not responsible for:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Flight delays, cancellations, or schedule changes</li>
                <li>Hotel overbooking or service quality issues</li>
                <li>Natural disasters or force majeure events</li>
                <li>Loss or damage to personal belongings</li>
                <li>Medical emergencies during travel</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Forex Services Terms</h2>
              <ul className="list-disc pl-6 mb-4">
                <li>Foreign exchange rates are subject to market fluctuations</li>
                <li>All forex transactions comply with RBI guidelines</li>
                <li>Valid ID proof and purpose documentation required</li>
                <li>Maximum limits apply as per RBI regulations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Governing Law</h2>
              <p className="mb-4">
                These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction 
                of courts in New Delhi, India.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h2>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Travel Wings Forex Private Limited</strong></p>
                <p>Corporate Office:</p>
                <p>105, 1st Floor, Pocket-B Market, Sarita Vihar, New Delhi-110076</p>
                <p>Tel: 011-41616942, 011-41716942, 011-42860239</p>
                <p>Email: info@travelwings.com</p>
                <p>Business Hours: Mon-Sat (10:00 AM - 6:00 PM)</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}